import RequestSvc from './RequestSvc'
import { API } from '../utils/Constants'

const DETAILS_ENDPOINT = 'https://pulpoia-ops.com/backend/ads/getadsdetailsforcardview'

const normalizeAdDetailsForSave = (detailsItem) => {
    if (!detailsItem || typeof detailsItem !== 'object') return null

    const normalized = { ...detailsItem }

    // Normalize publisherPlatform: replace empty array with "facebook"
    if (Array.isArray(normalized.publisherPlatform) && normalized.publisherPlatform.length === 0) {
        normalized.publisherPlatform = 'facebook'
    }

    // Transform keys to match backend schema
    if (normalized.link_url !== undefined) {
        normalized.ahref = normalized.link_url
        delete normalized.link_url
    }

    if (normalized.title !== undefined) {
        normalized.AdTitle = normalized.title
        delete normalized.title
    }

    // Add platform identifier
    normalized.FK_PlatformID = 1

    return normalized
}

/**
 * Fetches and normalizes ad details from backend so the object is suitable for saving.
 * Returns null when details are missing or the ad has been removed.
 */
export const fetchAdDetailsForSave = async (adOrLibraryId, { alertOnError = true } = {}) => {
    try {
        const libraryId = typeof adOrLibraryId === 'object' ? adOrLibraryId?.LibraryID : adOrLibraryId
        if (!libraryId) return null

        const url = `${DETAILS_ENDPOINT}?ids=${libraryId}`
        const response = await fetch(url)
        const result = await response.json()

        const hasData = Array.isArray(result?.data) && result.data.length > 0
        const firstItem = hasData ? result.data[0] : null

        // Contract: when no data, server may include { error: { message, failedIds } }
        if (!hasData && result?.error) {
            if (alertOnError) {
                alert('No se encontraron detalles válidos para el anuncio. Inténtalo más tarde.')
            }
            return null
        }

        const itemLevelError = Boolean(firstItem?.error) || Boolean(firstItem?.fails)
        if (!hasData || itemLevelError) {
            if (alertOnError) {
                alert('El anuncio fué eliminado de la Biblioteca de Facebook Ads Library')
            }
            return null
        }

        return normalizeAdDetailsForSave(firstItem)
    } catch (error) {
        console.error('[AdSaveSvc] Error fetching ad details:', error)
        if (alertOnError) {
            alert('No fue posible obtener los detalles del anuncio en este momento. Inténtalo nuevamente más tarde.')
        }
        return null
    }
}

const normalizeSavedAdForVaultGrid = (savedAd, { sourceAdObject, detailsLibraryId } = {}) => {
    if (!savedAd || typeof savedAd !== 'object') return null

    const normalized = savedAd
    normalized.Duplicated = normalized.Duplicated ?? sourceAdObject?.duplicates ?? 0
    normalized.Estatus = normalized.Estatus ?? normalized.Active ?? normalized.adsStatus ?? 1
    normalized.FK_Category_Code = normalized.FK_Category_Code ?? sourceAdObject?.FK_Category_Code
    normalized.LibraryID = normalized.LibraryID ?? detailsLibraryId
    return normalized
}

/**
 * Saves an ad to the dashboard API and returns a normalized object suitable for UserVaultGrid.
 * Returns null if save fails or if details cannot be fetched.
 */
export const saveAdToDashboard = async (adObject, { alertOnError = true } = {}) => {
    const adToSave = await fetchAdDetailsForSave(adObject, { alertOnError })
    if (!adToSave || !adToSave.LibraryID) {
        if (alertOnError) {
            alert('No se pudo guardar el anuncio porque no se obtuvieron los detalles requeridos.')
        }
        return null
    }

    adToSave.Duplicated = adObject?.duplicates
    adToSave.FK_Category_Code = adObject?.FK_Category_Code

    const request = new RequestSvc()
    const result = await request.post(API.DASHBOARD, adToSave)
    if (result?.error) {
        console.error('[AdSaveSvc] Save failed:', result)
        if (alertOnError) {
            alert('No se pudo guardar el anuncio. Inténtalo nuevamente más tarde.')
        }
        return null
    }

    const maybeSaved = result?.data?.data ?? result?.data
    return normalizeSavedAdForVaultGrid(maybeSaved, { sourceAdObject: adObject, detailsLibraryId: adToSave.LibraryID })
}
